/**
 * Garde-fou partagé sur les réponses de capture Payline (front web).
 *
 * Portage du module éprouvé de `primapp-native` (`src/utils/paymentGuard.js`).
 * Il verrouille le cas suivant, constaté en production sur les écrans Tournoi,
 * Événement et Activité du web :
 *
 * Quand Payline renvoie 01131 (challenge 3D Secure requis pour cette carte), le
 * resolver master répond en résolution NORMALE :
 *
 *     { resultCode: '01131', needs3DS: true, threeDSData: { ... } }
 *
 * — sans `transactionId` ni `authorizationNumber`. Le porteur n'a PAS été débité.
 * Un écran qui enchaîne sans inspecter ce cas construit une transaction aux
 * identifiants `undefined` et livre la prestation (inscription au tournoi, à
 * l'événement, à l'activité) sans le moindre euro encaissé. Silencieusement.
 *
 * `assertCaptured` transforme ce faux succès en échec explicite : la livraison
 * est bloquée et le golfeur reçoit une consigne actionnable (régler avec une
 * autre carte, ce qui repasse par le widget et l'authentification 3DS).
 *
 * Usage :
 *   import { assertCaptured, buildTransactionFields } from '_utils/paymentGuard';
 *
 *   .then((res) => {
 *     const captured = assertCaptured(res, 'Tournament.captureSubscriber');
 *     this.data.transaction = { ...buildTransactionFields(captured), amount, card };
 *     return this.setEntry();
 *   })
 */

/** Longueur de `master.sales_order_payment.call_no`. */
export const CALL_NO_MAX_LENGTH = 32;

export const PAYMENT_3DS_REQUIRED_MESSAGE =
  'Votre banque exige une authentification 3D Secure pour cette carte enregistrée. Veuillez régler avec une autre carte.';

export const PAYMENT_NOT_CAPTURED_MESSAGE = 'Le paiement n’a pas abouti. Aucun montant n’a été débité.';

/**
 * Renvoie `null` plutôt qu'une valeur qui ferait déborder `call_no`.
 * Miroir de `sanitizeCallNo` côté primagolf-master (src/services/utils.js) : le
 * numéro d'autorisation Payline fait 6 caractères, le token de session 33, et
 * l'y envoyer faisait échouer la commande APRÈS le débit (ER_DATA_TOO_LONG).
 */
export const sanitizeCallNo = (callNo) => {
  if (callNo === null || callNo === undefined) {
    return null;
  }
  const str = `${callNo}`.trim();
  return str.length > 0 && str.length <= CALL_NO_MAX_LENGTH ? str : null;
};

/**
 * Vérifie qu'une réponse de capture Payline correspond bien à un débit effectif.
 * Jette si le paiement n'a pas eu lieu, pour empêcher l'appelant de livrer une
 * prestation impayée.
 *
 * L'erreur porte `notCaptured: true` : l'appelant sait qu'AUCUN débit n'a eu lieu
 * et qu'il n'y a donc rien à rembourser (seulement, le cas échéant, une réservation
 * provisoire à libérer).
 */
export const assertCaptured = (res, context) => {
  if (!res) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${context}.noResult`);
    throw Object.assign(new Error(PAYMENT_NOT_CAPTURED_MESSAGE), { notCaptured: true });
  }

  if (res.needs3DS) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${context}.needs3DS`, res.resultCode);
    throw Object.assign(new Error(PAYMENT_3DS_REQUIRED_MESSAGE), {
      notCaptured: true,
      needs3DS: true,
    });
  }

  // `transactionId` (ou son alias Paybox `NUMTRANS`) est la seule preuve qu'une
  // transaction existe côté PSP. Absent = rien n'a été encaissé.
  if (!res.transactionId && !res.NUMTRANS) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${context}.noTransactionId`, res.resultCode);
    throw Object.assign(new Error(PAYMENT_NOT_CAPTURED_MESSAGE), { notCaptured: true });
  }

  return res;
};

/** Codes résultat Monext valant encaissement. */
export const SUCCESS_RESULT_CODES = ['00000', '02500'];

/**
 * Vérifie le verdict du WIDGET Payline hébergé, là où `assertCaptured` vérifie une
 * réponse de CAPTURE directe. Les deux ne sont pas interchangeables.
 *
 * `paylineCheckPayment` ne lève JAMAIS côté master : un paiement refusé revient en
 * résolution normale, avec `status: 'failed'` et un `code`. Et il porte quand même
 * un `transactionId` (Monext en attribue un aux transactions refusées), donc
 * `assertCaptured` le laisserait passer. C'est par ce trou qu'un rechargement
 * Practice pouvait être livré sans encaissement (cf. incident LAMMENS du 21/07 et
 * `primagolf-master/src/commands/recoverPracticePayment.js`).
 *
 * Deux formes de réponse transitent par ce même chemin :
 *  - `paylineCheckPayment` -> `{ status: 'success' | 'failed', code }`
 *  - `paylineCaptureWithToken` -> `{ resultCode }` (master a déjà levé si != 00000)
 * On accepte donc l'un OU l'autre verdict, et on exige une transaction.
 *
 * `02500` est conservé parce que primapp-embed le tolère déjà en production : on ne
 * durcit pas web au-delà de ce qui est accepté ailleurs aujourd'hui.
 */
export const assertWidgetPaymentSuccess = (res, context) => {
  if (!res) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${context}.noResult`);
    throw Object.assign(new Error(PAYMENT_NOT_CAPTURED_MESSAGE), { notCaptured: true });
  }

  const resultCode = res.code || res.resultCode || null;
  const accepted = res.status === 'success' || (resultCode && SUCCESS_RESULT_CODES.includes(resultCode));

  if (!accepted) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${context}.notSuccessful`, { status: res.status, resultCode });
    throw Object.assign(new Error(PAYMENT_NOT_CAPTURED_MESSAGE), { notCaptured: true });
  }

  if (!res.transactionId && !res.NUMTRANS) {
    // eslint-disable-next-line no-console
    console.error(`[payment] ${context}.noTransactionId`, { status: res.status, resultCode });
    throw Object.assign(new Error(PAYMENT_NOT_CAPTURED_MESSAGE), { notCaptured: true });
  }

  return res;
};

/**
 * Construit les champs de transaction issus de la réponse Payline.
 * `callNo` reçoit le numéro d'autorisation (6 car.), jamais le token de session.
 */
export const buildTransactionFields = (res) => ({
  callNo: sanitizeCallNo(res.authorizationNumber || res.NUMAPPEL),
  transactionNo: res.transactionId || res.NUMTRANS,
});

export default {
  CALL_NO_MAX_LENGTH,
  PAYMENT_3DS_REQUIRED_MESSAGE,
  PAYMENT_NOT_CAPTURED_MESSAGE,
  SUCCESS_RESULT_CODES,
  sanitizeCallNo,
  assertCaptured,
  assertWidgetPaymentSuccess,
  buildTransactionFields,
};
